/**
 * Font Map for AI Presentation Layer Editing
 *
 * Maps natural language font names and style keywords to FlowVella font names.
 * FlowVella uses iOS font naming conventions.
 */

/**
 * Font family mapping from natural language to FlowVella font names
 */
export const FONT_FAMILIES: Record<string, string> = {
  // Sans-serif fonts
  'helvetica': 'HelveticaNeue',
  'helvetica neue': 'HelveticaNeue',
  'arial': 'ArialMT',
  'san francisco': 'SFProText-Regular',
  'sf pro': 'SFProText-Regular',
  'avenir': 'AvenirNext-Regular',
  'avenir next': 'AvenirNext-Regular',
  'futura': 'Futura-Medium',
  'gill sans': 'GillSans',
  'verdana': 'Verdana',
  'trebuchet': 'TrebuchetMS',
  'tahoma': 'Tahoma',

  // Serif fonts
  'times': 'TimesNewRomanPSMT',
  'times new roman': 'TimesNewRomanPSMT',
  'georgia': 'Georgia',
  'palatino': 'Palatino-Roman',
  'baskerville': 'Baskerville',
  'didot': 'Didot',
  'bodoni': 'BodoniSvtyTwoITCTT-Book',
  'garamond': 'AppleGaramond',

  // Monospace fonts
  'courier': 'Courier',
  'courier new': 'CourierNewPSMT',
  'menlo': 'Menlo-Regular',
  'monaco': 'Monaco',
  'consolas': 'Consolas',
  'sf mono': 'SFMono-Regular',

  // Decorative fonts
  'marker felt': 'MarkerFelt-Wide',
  'chalkboard': 'Chalkboard-Bold',
  'papyrus': 'Papyrus',
  'copperplate': 'Copperplate',
  'impact': 'Impact',
  'comic sans': 'ComicSansMS',

  // Script/handwriting fonts
  'brush script': 'BrushScriptMT',
  'snell roundhand': 'SnellRoundhand',
  'zapfino': 'Zapfino',
};

/**
 * Font style suffixes for weight/style variations
 */
export const FONT_STYLE_SUFFIXES: Record<string, string[]> = {
  // Weight variations
  'bold': ['-Bold', '-Black', '-Heavy'],
  'light': ['-Light', '-Thin', '-UltraLight'],
  'medium': ['-Medium', '-DemiBold'],
  'regular': ['-Regular', '', '-Roman'],
  'thin': ['-Thin', '-UltraLight'],
  'black': ['-Black', '-Heavy'],
  'heavy': ['-Heavy', '-Black'],

  // Style variations
  'italic': ['-Italic', '-Oblique', 'Italic'],
  'oblique': ['-Oblique', '-Italic'],

  // Combined
  'bold italic': ['-BoldItalic', '-BoldOblique'],
  'light italic': ['-LightItalic', '-LightOblique'],

  // Condensed
  'condensed': ['-Condensed', 'Condensed'],
  'condensed bold': ['-CondensedBold', 'CondensedBold'],
};

/**
 * Complete font name mapping including style variations
 */
export const FONT_MAP: Record<string, string> = {
  // Helvetica family
  'helvetica': 'HelveticaNeue',
  'helvetica bold': 'HelveticaNeue-Bold',
  'helvetica light': 'HelveticaNeue-Light',
  'helvetica italic': 'HelveticaNeue-Italic',
  'helvetica bold italic': 'HelveticaNeue-BoldItalic',
  'helvetica medium': 'HelveticaNeue-Medium',
  'helvetica thin': 'HelveticaNeue-Thin',
  'helvetica condensed': 'HelveticaNeue-CondensedBold',
  'helvetica condensed bold': 'HelveticaNeue-CondensedBold',

  // Arial family
  'arial': 'ArialMT',
  'arial bold': 'Arial-BoldMT',
  'arial italic': 'Arial-ItalicMT',
  'arial bold italic': 'Arial-BoldItalicMT',

  // Times family
  'times': 'TimesNewRomanPSMT',
  'times new roman': 'TimesNewRomanPSMT',
  'times bold': 'TimesNewRomanPS-BoldMT',
  'times italic': 'TimesNewRomanPS-ItalicMT',
  'times bold italic': 'TimesNewRomanPS-BoldItalicMT',

  // Georgia family
  'georgia': 'Georgia',
  'georgia bold': 'Georgia-Bold',
  'georgia italic': 'Georgia-Italic',
  'georgia bold italic': 'Georgia-BoldItalic',

  // Avenir family
  'avenir': 'AvenirNext-Regular',
  'avenir next': 'AvenirNext-Regular',
  'avenir bold': 'AvenirNext-Bold',
  'avenir medium': 'AvenirNext-Medium',
  'avenir light': 'AvenirNext-UltraLight',
  'avenir italic': 'AvenirNext-Italic',
  'avenir bold italic': 'AvenirNext-BoldItalic',
  'avenir demibold': 'AvenirNext-DemiBold',
  'avenir heavy': 'AvenirNext-Heavy',

  // Futura family
  'futura': 'Futura-Medium',
  'futura bold': 'Futura-Bold',
  'futura light': 'Futura-Light',
  'futura medium': 'Futura-Medium',
  'futura condensed': 'Futura-CondensedMedium',

  // Courier family
  'courier': 'Courier',
  'courier new': 'CourierNewPSMT',
  'courier bold': 'Courier-Bold',
  'courier italic': 'Courier-Oblique',

  // Menlo family (monospace)
  'menlo': 'Menlo-Regular',
  'menlo bold': 'Menlo-Bold',
  'menlo italic': 'Menlo-Italic',

  // Gill Sans family
  'gill sans': 'GillSans',
  'gill sans bold': 'GillSans-Bold',
  'gill sans light': 'GillSans-Light',
  'gill sans italic': 'GillSans-Italic',

  // Palatino family
  'palatino': 'Palatino-Roman',
  'palatino bold': 'Palatino-Bold',
  'palatino italic': 'Palatino-Italic',

  // Baskerville family
  'baskerville': 'Baskerville',
  'baskerville bold': 'Baskerville-Bold',
  'baskerville italic': 'Baskerville-Italic',
};

/**
 * Default font for different contexts
 */
export const DEFAULT_FONTS = {
  title: 'HelveticaNeue-Bold',
  subtitle: 'HelveticaNeue',
  body: 'HelveticaNeue',
  quote: 'Georgia-Italic',
  code: 'Menlo-Regular',
  caption: 'HelveticaNeue-Light',
};

/**
 * Get a font name from natural language input
 * Returns null if no matching font found
 */
export function parseFont(fontString: string): string | null {
  if (!fontString) return null;

  const normalized = fontString.toLowerCase().trim();

  // Check direct mapping first
  if (FONT_MAP[normalized]) {
    return FONT_MAP[normalized];
  }

  // Check if it's already a valid font name (passthrough)
  if (normalized.includes('-') || normalized.includes('MT')) {
    return fontString; // Return as-is if it looks like a system font name
  }

  // Try to find family match
  for (const [key, value] of Object.entries(FONT_FAMILIES)) {
    if (normalized.includes(key)) {
      return value;
    }
  }

  return null;
}

/**
 * Apply a style modifier to a base font name
 * Returns the modified font name or the original if style not applicable
 */
export function applyFontStyle(baseFontName: string, style: string): string {
  const normalizedStyle = style.toLowerCase().trim();

  // Get the style suffixes to try
  const suffixes = FONT_STYLE_SUFFIXES[normalizedStyle];
  if (!suffixes) return baseFontName;

  // Get the base font family (remove any existing style suffix)
  let baseFamily = baseFontName;
  for (const suffix of Object.values(FONT_STYLE_SUFFIXES).flat()) {
    if (baseFontName.endsWith(suffix)) {
      baseFamily = baseFontName.slice(0, -suffix.length);
      break;
    }
  }

  // Try each suffix to find a valid font
  for (const suffix of suffixes) {
    const testFont = baseFamily + suffix;
    // In a real implementation, we'd validate against available fonts
    // For now, just return the first attempt
    return testFont;
  }

  return baseFontName;
}

/**
 * Check if a font name represents a bold variant
 */
export function isBoldFont(fontName: string): boolean {
  const lower = fontName.toLowerCase();
  return lower.includes('bold') || lower.includes('black') || lower.includes('heavy');
}

/**
 * Check if a font name represents an italic variant
 */
export function isItalicFont(fontName: string): boolean {
  const lower = fontName.toLowerCase();
  return lower.includes('italic') || lower.includes('oblique');
}

/**
 * Get the regular (non-bold, non-italic) variant of a font
 */
export function getRegularVariant(fontName: string): string {
  // Common suffixes to remove
  const styleSuffixes = [
    '-Bold', '-BoldItalic', '-BoldOblique',
    '-Italic', '-Oblique',
    '-Light', '-LightItalic',
    '-Medium', '-MediumItalic',
    '-Heavy', '-Black',
    '-Thin', '-UltraLight',
    '-CondensedBold', '-Condensed',
  ];

  let result = fontName;
  for (const suffix of styleSuffixes) {
    if (result.endsWith(suffix)) {
      result = result.slice(0, -suffix.length);
      break;
    }
  }

  // Add regular suffix if needed
  if (result === fontName && !result.includes('-')) {
    return result + '-Regular';
  }

  return result || fontName;
}

/**
 * Describe a font in natural language
 */
export function describeFontStyle(fontName: string): string {
  const parts: string[] = [];

  if (isBoldFont(fontName)) parts.push('bold');
  if (isItalicFont(fontName)) parts.push('italic');
  if (fontName.toLowerCase().includes('light')) parts.push('light');
  if (fontName.toLowerCase().includes('condensed')) parts.push('condensed');

  // Get family name
  for (const [name, value] of Object.entries(FONT_FAMILIES)) {
    if (fontName.toLowerCase().includes(value.toLowerCase().split('-')[0])) {
      parts.unshift(name);
      break;
    }
  }

  return parts.length > 0 ? parts.join(' ') : fontName;
}
